/**
 * Session Timeout Utility
 * ----------------------------------------------------------------------------
 * Enforces an inactivity-based session timeout. The application previously
 * kept sessions alive indefinitely as long as the auth token was valid,
 * regardless of user inactivity. This utility tracks user activity and
 * automatically terminates the session after a configurable period of
 * inactivity, forcing re-authentication.
 *
 * Security rationale (Application Security Assessment - "No Session Timeout"):
 * ----------------------------------------------------------------------------
 * Without an inactivity timeout, a stolen/leaked session token remains usable
 * indefinitely. By enforcing a timeout, we reduce the window in which an
 * attacker can reuse a captured token and align with OWASP Session Management
 * best practices (Session Timeout / Idle Timeout).
 *
 * Behavior:
 *  - Listens for user activity events (mouse, keyboard, touch, scroll).
 *  - After `timeoutMs` of inactivity, shows a warning (via callback).
 *  - After `warningMs` more, forcibly ends the session (clears storage/cookies
 *    and reloads the page to trigger re-authentication).
 *  - The user can "continue" the session to reset the timer.
 *
 * Cross-tab coordination:
 *  - Uses localStorage + the "storage" event so that activity in ANY tab of
 *    the same origin keeps the session alive in ALL tabs. This prevents a
 *    user who is actively working in another tab from being logged out of
 *    the CAM Copilot tab.
 */

import { endSessionOnServer, getRegisteredUser } from "./sessionManager";

const DEFAULT_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes of inactivity
const DEFAULT_WARNING_MS = 60 * 1000; // warn 60 seconds before timeout

// localStorage key used to share the last-activity timestamp across tabs
const LAST_ACTIVITY_KEY = "cam_copilot_last_activity";

// Events that indicate user activity
const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "touchstart",
  "scroll",
  "wheel",
  "click",
];

/**
 * Clears all authentication-related state from the browser.
 * - sessionStorage: MSAL cache + idToken/exp
 * - Cookies: userToken, userName, referrer (used in iframe/embedded mode)
 */
export function clearSession() {
  try {
    // Clear sessionStorage (MSAL cache + app tokens)
    sessionStorage.clear();
  } catch (e) {
    console.warn("[SessionTimeout] Failed to clear sessionStorage:", e);
  }

  // Clear the cross-tab activity marker
  try {
    localStorage.removeItem(LAST_ACTIVITY_KEY);
  } catch (e) {
    console.warn("[SessionTimeout] Failed to clear localStorage activity marker:", e);
  }

  // Clear authentication cookies
  const cookiesToClear = ["userToken", "userName", "referrer"];
  cookiesToClear.forEach((name) => {
    try {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
    } catch (e) {
      console.warn(`[SessionTimeout] Failed to clear cookie "${name}":`, e);
    }
  });
}

/**
 * Ends the session: clears all auth state and reloads the page so the
 * application re-runs its authentication flow.
 */
export async function endSession() {
  console.warn("[SessionTimeout] Session expired due to inactivity. Logging out.");

  // Notify the backend BEFORE clearing sessionStorage so the Redis active
  // session record for this user is removed (the next sign-in gets a clean
  // slate).
  const registeredUser = getRegisteredUser();
  if (registeredUser) {
    try {
      await endSessionOnServer(registeredUser);
    } catch (e) {
      console.warn("[SessionTimeout] Could not notify backend of logout:", e);
    }
  }

  clearSession();
  // Reload to force re-authentication. Use a small delay so any pending
  // UI updates (e.g. warning modal) can render first.
  window.location.reload();
}

/**
 * Records the current time as the "last activity" timestamp in localStorage.
 * This is shared across all tabs of the same origin so that activity in one
 * tab keeps the session alive in the others.
 */
function recordActivity() {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
  } catch (e) {
    // localStorage may be unavailable (e.g. private mode); fall back to
    // per-tab-only tracking, which is the previous behavior.
  }
}

/**
 * Reads the most recent "last activity" timestamp shared across tabs.
 * @returns {number} timestamp in ms, or 0 if none recorded.
 */
function getLastActivity() {
  try {
    const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
    const ts = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(ts) ? ts : 0;
  } catch (e) {
    return 0;
  }
}

/**
 * Starts the inactivity-based session timeout monitor.
 *
 * @param {Object} options
 * @param {number} [options.timeoutMs] - Max inactivity before session ends (ms).
 * @param {number} [options.warningMs] - How long before timeout to show warning (ms).
 * @param {Function} [options.onWarning] - Called when the warning period begins.
 * @param {Function} [options.onExpire] - Called when the session times out.
 * @param {Function} [options.onContinue] - Called when the user continues the session.
 * @returns {Function} A cleanup function that stops the monitor.
 */
export function startSessionTimeout({
  timeoutMs = DEFAULT_TIMEOUT_MS,
  warningMs = DEFAULT_WARNING_MS,
  onWarning = () => {},
  onExpire = endSession,
  onContinue = () => {},
} = {}) {
  let timeoutId = null;
  let warningId = null;

  const clearTimers = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    if (warningId) {
      clearTimeout(warningId);
      warningId = null;
    }
  };

  const resetTimer = () => {
    clearTimers();

    // Schedule the warning
    warningId = setTimeout(() => {
      onWarning();
    }, Math.max(0, timeoutMs - warningMs));

    // Schedule the hard timeout
    timeoutId = setTimeout(() => {
      onExpire();
    }, timeoutMs);
  };

  const handleActivity = () => {
    recordActivity();
    resetTimer();
  };

  // Listen for activity in THIS tab
  ACTIVITY_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, handleActivity, { passive: true });
  });

  // Listen for activity recorded in OTHER tabs of the same origin.
  // The "storage" event fires in every other tab when localStorage changes.
  const handleStorage = (event) => {
    if (event.key === LAST_ACTIVITY_KEY && event.newValue) {
      // Another tab recorded activity; reset this tab's timer too.
      resetTimer();
    }
  };
  window.addEventListener("storage", handleStorage);

  // On startup, if another tab has recorded activity more recently than this
  // tab's own timer would assume, sync to that timestamp so we don't
  // immediately time out a session that is active elsewhere.
  const lastActivity = getLastActivity();
  if (lastActivity > 0) {
    const elapsed = Date.now() - lastActivity;
    if (elapsed < timeoutMs) {
      // Session is still active elsewhere; start our timer from the shared
      // timestamp so all tabs stay in sync.
      const remaining = timeoutMs - elapsed;
      warningId = setTimeout(() => onWarning(), Math.max(0, remaining - warningMs));
      timeoutId = setTimeout(() => onExpire(), remaining);
    }
  } else {
    // No shared activity yet; record our own and start fresh.
    recordActivity();
    resetTimer();
  }

  // Return a cleanup function
  return () => {
    clearTimers();
    ACTIVITY_EVENTS.forEach((eventName) => {
      window.removeEventListener(eventName, handleActivity);
    });
    window.removeEventListener("storage", handleStorage);
  };
}

/**
 * Resets the session timer (used by the "Continue Session" button in the
 * warning modal). This is a thin wrapper so the modal doesn't need to know
 * about the internal timer implementation.
 */
export function continueSession() {
  // Record activity and reset the timer. Also dispatch a synthetic activity
  // event so any other tabs listening on the storage event stay in sync.
  recordActivity();
  window.dispatchEvent(new Event("mousemove"));
}